import { cn } from 'noddi-ui-common';
import { type ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

type Props = {
  /** Accessible name when it differs from the visible text, such as a trailing emoji. */
  label?: string;
  className?: string;
  children: ReactNode;
};

/** Field a screen focuses on mount for typing (phone, email). Taking it would cost the customer a tap. */
const TYPING_TARGET = 'input, textarea, select, [contenteditable="true"]';

/**
 * Page h1 that takes focus when the route changes, so screen readers start on the new page
 * instead of the old page's trigger. Invisible to pointer users: no outline, no scroll.
 */
export const PageHeading = ({ label, className, children }: Props) => {
  const { pathname } = useLocation();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (document.activeElement?.matches(TYPING_TARGET)) {
      return;
    }
    headingRef.current?.focus({ preventScroll: true });
  }, [pathname]);

  return (
    <h1 ref={headingRef} tabIndex={-1} aria-label={label} className={cn('focus:outline-hidden', className)}>
      {children}
    </h1>
  );
};
